import { useState, useRef, useEffect, useId } from 'preact/hooks'
import { ArrowUp, Paperclip, X } from 'lucide-preact'
import type { WebsterAttachment } from '@/types/webster'
import type { AgentEffort } from '@/api/asset'
import { Select } from '@/components/common/Select'
import { GENERATION_EFFORT_LEVELS, type GenerationEffortControl } from '@/components/webster/generationEffort'

interface WebsterChatInputProps {
    onSend: (message: string, attachments?: WebsterAttachment[]) => void
    disabled?: boolean
    buttonLabel?: string
    allowEmpty?: boolean
    placeholder?: string
    /**
     * When set, the input is locked because *another user* is mid-turn on the
     * shared asset chat (LP / Email asset). Disables the textarea + send button
     * and attributes the wait in the placeholder, e.g. "Joel is asking Webster…".
     * Distinct from `disabled` which means "this user's own turn is in flight" —
     * the spinner styling stays the same in both cases.
     */
    lockedByUserName?: string | null
    /**
     * When set, the input is locked while Webster waits on the user for something else, and the
     * placeholder says what. Nobody is working, so the send button does not spin.
     */
    lockedReason?: string | null
    /**
     * When provided, the composer shows an image-attach affordance (paperclip).
     * Picks are validated client-side (image type + 5MB) then uploaded via this
     * callback, which returns the stored URL. Parents that omit it — e.g. the
     * pre-generation "Generate" setup input — get the original text-only composer
     * unchanged.
     */
    onUploadImage?: (file: File) => Promise<{ url: string; fileName?: string; contentType?: string }>
    /**
     * When provided, the composer shows the effort picker bottom-left and the hint line leads with
     * the chosen level's description. Only the Webster builder has an asset to store it on.
     */
    generationEffort?: GenerationEffortControl | null
}

const EFFORT_OPTIONS = GENERATION_EFFORT_LEVELS.map(level => ({ value: level.value, label: level.value }))
const KEY_HINT = 'Enter to send · Shift+Enter for new line'

const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024

export function WebsterChatInput({
    onSend,
    disabled,
    buttonLabel,
    allowEmpty = false,
    placeholder = 'Type a message...',
    lockedByUserName,
    lockedReason,
    onUploadImage,
    generationEffort,
}: WebsterChatInputProps) {
    const [message, setMessage] = useState('')
    const [pendingAttachments, setPendingAttachments] = useState<WebsterAttachment[]>([])
    const [uploading, setUploading] = useState(false)
    const [attachError, setAttachError] = useState<string | null>(null)
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const effortSelectId = useId()

    // Effective disable: own turn (disabled), another user's turn (lockedByUserName), or Webster
    // waiting on the user (lockedReason). Only the first two mean anyone is working.
    const lockedByOther = !!lockedByUserName
    const working = !!disabled || lockedByOther
    const effectiveDisabled = working || !!lockedReason
    const effectivePlaceholder = lockedByOther
        ? `${lockedByUserName} is asking Webster…`
        : (disabled ? 'Webster is working...' : (lockedReason || placeholder))
    const hasAttachments = pendingAttachments.length > 0
    // Attachments alone are enough to send — "add this image" is a valid turn.
    const savingEffort = generationEffort?.saving ?? false
    const canSubmit = !effectiveDisabled && !uploading && !savingEffort && (allowEmpty || message.trim().length > 0 || hasAttachments)
    const effortDescription = generationEffort
        ? GENERATION_EFFORT_LEVELS.find(level => level.value === generationEffort.value)?.description
        : undefined

    useEffect(() => {
        if (!effectiveDisabled && textareaRef.current) {
            textareaRef.current.focus()
        }
    }, [effectiveDisabled])

    const handleSubmit = (e: Event) => {
        e.preventDefault()
        if (!canSubmit) return
        onSend(message.trim(), hasAttachments ? pendingAttachments : undefined)
        setMessage('')
        setPendingAttachments([])
        setAttachError(null)
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto'
        }
    }

    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSubmit(e)
        }
    }

    const handleInput = (e: Event) => {
        const target = e.target as HTMLTextAreaElement
        setMessage(target.value)
        target.style.height = 'auto'
        target.style.height = `${Math.min(target.scrollHeight, 160)}px`
    }

    const handleAttachClick = () => {
        setAttachError(null)
        fileInputRef.current?.click()
    }

    const handleFileChange = async (e: Event) => {
        const target = e.target as HTMLInputElement
        const file = target.files?.[0]
        // Reset so picking the same file again re-triggers change.
        target.value = ''
        if (!file || !onUploadImage) return

        if (!file.type.startsWith('image/')) {
            setAttachError('Please select an image file')
            return
        }
        if (file.size > MAX_ATTACHMENT_BYTES) {
            setAttachError('Image must be under 5MB')
            return
        }

        setAttachError(null)
        setUploading(true)
        try {
            const result = await onUploadImage(file)
            setPendingAttachments(prev => [
                ...prev,
                { url: result.url, fileName: result.fileName ?? file.name, contentType: result.contentType ?? file.type },
            ])
        } catch {
            setAttachError('Failed to upload image. Please try again.')
        } finally {
            setUploading(false)
        }
    }

    const handleNoteChange = (index: number, note: string) => {
        setPendingAttachments(prev => prev.map((a, i) => (i === index ? { ...a, note } : a)))
    }

    const handleRemoveAttachment = (index: number) => {
        setPendingAttachments(prev => prev.filter((_, i) => i !== index))
    }

    return (
        <div className="px-4 py-3 bg-white dark:bg-slate-900">
            <div className="max-w-3xl mx-auto">
                <div
                    className={`
                        relative rounded-2xl border transition-colors
                        ${effectiveDisabled
                            ? 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800'
                            : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500'
                        }
                    `}
                >
                    {/* Pending attachments strip — sits above the textarea inside the composer. */}
                    {hasAttachments && (
                        <div className="flex flex-wrap gap-2 px-3 pt-3">
                            {pendingAttachments.map((att, i) => (
                                <div
                                    key={`${att.url}-${i}`}
                                    className="flex items-start gap-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/60 p-2 w-[240px] max-w-full"
                                >
                                    <img
                                        src={att.url}
                                        alt={att.fileName || 'attachment'}
                                        className="h-12 w-12 rounded object-cover border border-slate-200 dark:border-slate-600 bg-white shrink-0"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className="text-xs text-slate-600 dark:text-slate-300 truncate" title={att.fileName}>
                                            {att.fileName || 'Image'}
                                        </div>
                                        <input
                                            type="text"
                                            value={att.note ?? ''}
                                            disabled={effectiveDisabled}
                                            placeholder="Note (optional) — where it goes"
                                            onInput={(e) => handleNoteChange(i, (e.target as HTMLInputElement).value)}
                                            className="mt-1 w-full text-xs px-2 py-1 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveAttachment(i)}
                                        disabled={effectiveDisabled}
                                        className="p-0.5 rounded text-slate-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                                        aria-label="Remove attachment"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    <textarea
                        ref={textareaRef}
                        value={message}
                        onInput={handleInput}
                        onKeyDown={handleKeyDown}
                        placeholder={effectivePlaceholder}
                        disabled={effectiveDisabled}
                        rows={1}
                        className={`w-full px-4 pt-3 ${generationEffort ? 'pb-12' : 'pb-10'} bg-transparent text-slate-900 dark:text-slate-100
                            focus:outline-none resize-none disabled:text-slate-400
                            text-sm placeholder:text-slate-400 dark:placeholder:text-slate-500`}
                        style={{ minHeight: '52px', maxHeight: '160px' }}
                    />
                    {/* This row spans the textarea's bottom padding. pointer-events-none keeps a click there
                        focusing the textarea; only the controls inside take clicks. */}
                    <div className="absolute bottom-2 inset-x-2 flex items-center gap-2 pointer-events-none">
                        {generationEffort && (
                            <Select
                                name={effortSelectId}
                                label="Effort"
                                labelClassName="sr-only"
                                density="compact"
                                options={EFFORT_OPTIONS}
                                placeholder="Effort"
                                value={generationEffort.value}
                                disabled={effectiveDisabled || savingEffort}
                                onChange={(e: Event) => generationEffort.onChange((e.target as HTMLSelectElement).value as AgentEffort)}
                                className="disabled:opacity-50 disabled:cursor-not-allowed"
                                containerClassName="w-32 pointer-events-auto"
                            />
                        )}
                        <div className="ml-auto flex items-center gap-2 pointer-events-auto">
                            {buttonLabel && (
                                <span className="text-xs text-slate-400 dark:text-slate-500 mr-1">{buttonLabel}</span>
                            )}
                            {onUploadImage && (
                                <>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        className="hidden"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleAttachClick}
                                        disabled={effectiveDisabled || uploading}
                                        className="p-1.5 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        aria-label="Attach image"
                                        title="Attach image"
                                    >
                                        {uploading ? (
                                            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                            <Paperclip className="w-4 h-4" />
                                        )}
                                    </button>
                                </>
                            )}
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={!canSubmit}
                                className={`
                                    p-1.5 rounded-lg transition-colors
                                    ${canSubmit
                                        ? 'bg-primary-600 hover:bg-primary-700 text-white'
                                        : 'bg-slate-200 dark:bg-slate-600 text-slate-400 dark:text-slate-500 cursor-not-allowed'
                                    }
                                `}
                                aria-label="Send message"
                            >
                                {working ? (
                                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    <ArrowUp className="w-4 h-4" />
                                )}
                            </button>
                        </div>
                    </div>
                </div>
                {attachError && (
                    <p className="mt-1.5 text-xs text-center text-red-500 dark:text-red-400">{attachError}</p>
                )}
                <p className="mt-1.5 text-xs text-center text-slate-400 dark:text-slate-500">
                    {effortDescription ? `${effortDescription} · ${KEY_HINT}` : `Press ${KEY_HINT}`}
                </p>
            </div>
        </div>
    )
}
